import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { AdminFilterService } from './admin-filter.service';
import {
  AdminAuditEventListData,
  AdminAuditFilterKey,
  ApiEnvelope,
  ApproveExceptionData,
  DenialReasonsReportData,
  DenyExceptionData,
  ExceptionDashboardData,
  ExceptionDetailData,
  ExceptionQueueData,
  ExceptionQueueStatus,
  RemoveExceptionData,
} from '@core/models';

/**
 * Admin/ops API client for the exception dashboard, queue, and decision endpoints
 * (ops-dashboard-001 through ops-deny-001).
 */
@Injectable({ providedIn: 'root' })
export class ExceptionAdminService {
  private readonly api = inject(ApiService);
  private readonly filters = inject(AdminFilterService);

  /**
   * GET /admin/exceptions/dashboard with the current AdminFilterService range.
   */
  getDashboard(): Observable<ApiEnvelope<ExceptionDashboardData>> {
    const params = this.filters.queryParams();
    const qs = new URLSearchParams();
    qs.set('range', params.range);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.api.get<ExceptionDashboardData>(`/admin/exceptions/dashboard?${qs.toString()}`);
  }

  /**
   * GET /admin/reports/denial-reasons — Denial Reasons Report data
   * scoped by AdminFilterService range (ops-reports-001).
   */
  getDenialReasonsReport(): Observable<ApiEnvelope<DenialReasonsReportData>> {
    const params = this.filters.queryParams();
    const qs = new URLSearchParams();
    qs.set('range', params.range);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.api.get<DenialReasonsReportData>(`/admin/reports/denial-reasons?${qs.toString()}`);
  }

  /**
   * GET /admin/exceptions — request queue with status/search filters
   * scoped by AdminFilterService range (ops-queue-001).
   */
  getQueue(
    status: ExceptionQueueStatus,
    search: string,
  ): Observable<ApiEnvelope<ExceptionQueueData>> {
    const params = this.filters.queryParams();
    const qs = new URLSearchParams();
    qs.set('status', status);
    if (search.trim()) qs.set('search', search.trim());
    qs.set('range', params.range);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.api.get<ExceptionQueueData>(`/admin/exceptions?${qs.toString()}`);
  }

  /**
   * GET /admin/exceptions/:id — detail drawer payload (ops-drawer-001).
   */
  getDetail(id: string): Observable<ApiEnvelope<ExceptionDetailData>> {
    return this.api.get<ExceptionDetailData>(`/admin/exceptions/${encodeURIComponent(id)}`);
  }

  /**
   * GET /admin/exceptions/:id/document/pdf — download confirmation PDF (generates on demand).
   */
  downloadExceptionDocumentPdf(exceptionId: string): Observable<Blob> {
    return this.api.getBlob(`/admin/exceptions/${encodeURIComponent(exceptionId)}/document/pdf`);
  }

  /**
   * POST /admin/exceptions/:id/approve — one-click queue approval.
   */
  approve(id: string): Observable<ApiEnvelope<ApproveExceptionData>> {
    return this.api.post<ApproveExceptionData>(`/admin/exceptions/${id}/approve`, {});
  }

  /**
   * POST /admin/exceptions/:id/deny — used by the detail drawer (ops-deny-001).
   */
  deny(id: string, failureReason: string): Observable<ApiEnvelope<DenyExceptionData>> {
    return this.api.post<DenyExceptionData>(`/admin/exceptions/${id}/deny`, { failureReason });
  }

  /**
   * DELETE /admin/exceptions/:id — super-admin remove of test/QA exceptions (LSVP-102).
   */
  remove(id: string): Observable<ApiEnvelope<RemoveExceptionData>> {
    return this.api.delete<RemoveExceptionData>(`/admin/exceptions/${encodeURIComponent(id)}`);
  }

  /**
   * GET /admin/audit-events — system logs filtered by type and current
   * AdminFilterService range (ops-logs-001).
   */
  getAuditEvents(type: AdminAuditFilterKey): Observable<ApiEnvelope<AdminAuditEventListData>> {
    const params = this.filters.queryParams();
    const qs = new URLSearchParams();
    qs.set('type', type);
    qs.set('range', params.range);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return this.api.get<AdminAuditEventListData>(`/admin/audit-events?${qs.toString()}`);
  }
}
